import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * "September 7, 2026" in the RUNTIME's zone, because it names none.
 *
 * NOT FOR /admin. Every admin cell reads in the owner's Central zone, and
 * Vercel renders in UTC, so this formatter put a 9:30 PM Central timestamp on
 * the NEXT day on the deployed dashboard while a pinned cell beside it showed
 * the right one. Its admin call sites now go through
 * `web/app/(admin)/admin/_components/admin-format.ts` (`fmtDate` short,
 * `fmtDateTime` with a clock), all pinned.
 *
 * It stays for its two CUSTOMER-facing callers, where the viewer's own zone is
 * the right zone rather than a bug: the purchase history on /app/settings and
 * the pause-until date in the email preference center.
 */
export function formatDate(date: Date | string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(date))
}

/**
 * Parse a comma-separated email allowlist env value into a lowercased,
 * trimmed, blank-filtered list. One shared parser for every admin-email
 * allowlist in the repo (owner_identity's dashboard admin list, auth.ts's
 * George-only tier, internal-email.ts's lead-pipeline exclusion list) —
 * this exact class of "two silently-inconsistent independent 'resolve the
 * owner' implementations" already broke every ops_decision_queue card once
 * (see owner-identity.ts), so the parsing primitive underneath every one of
 * these lists stays a single function rather than three copies that could
 * drift the same way.
 */
export function parseEmailAllowlist(raw: string | null | undefined): string[] {
  return (raw ?? "")
    .split(",")
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean)
}

/**
 * The one email-shape test for every form, route and sender in the repo.
 *
 * A FORMAT check, never a reachability check: something before the @, something
 * after it, a dot in the domain, no whitespace anywhere. A false negative on a
 * checkout or an application loses a paying customer at the last step, so this
 * stays deliberately loose and no surface tightens it locally. It used to be
 * declared byte-for-byte in 23 files; one copy drifting would have accepted an
 * address on one surface and rejected the same address on the next.
 */
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Truncate free text to at most `max` UTF-16 units without splitting a
 * character. A bare `.slice(0, n)` can cut an emoji in half, leaving a lone
 * surrogate that Postgres rejects (22P02) on the next upsert. Whole grapheme
 * clusters are kept (a flag or family emoji is never half-cut), NUL characters
 * are removed because Postgres text cannot hold them, and any lone surrogate
 * already in the input is replaced rather than carried through.
 */
export function truncateText(raw: string, max: number): string {
  const text = raw.replace(/\u0000/g, "")
  const clean = typeof text.toWellFormed === "function" ? text.toWellFormed() : text
  if (clean.length <= max) return clean
  const parts: Iterable<string> =
    typeof Intl !== "undefined" && "Segmenter" in Intl
      ? Array.from(new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(clean), (x) => x.segment)
      : Array.from(clean)
  let out = ""
  for (const part of parts) {
    if (out.length + part.length > max) break
    out += part
  }
  return out
}

/**
 * Trimmed, lowercased and validated, or null. Takes `unknown` so a route can
 * hand it a raw JSON field: a non-string answers null, exactly what the routes'
 * old `typeof value === "string" ? ... : ""` guards produced. 254 is the RFC
 * 5321 ceiling on an address and is measured AFTER the trim.
 *
 * This is the validate-or-null shape (newsletter, register, account, the
 * membership checkout, the opt-out ledger, the lead ingest). The
 * trim-and-lowercase JOIN KEY used to match a row by email is a different job,
 * `normalizeEmailKey` below: it must never reject, because a stored address
 * that fails this test still has to match its own row.
 */
export function normalizeEmailAddress(raw: unknown): string | null {
  // Invisible format characters (zero-width space, BOM, joiners, bidi marks)
  // ride along when an address is pasted in Safari and pass EMAIL_RE, so they
  // are stripped, and NFC makes a composed and decomposed spelling one address.
  const value =
    typeof raw === "string" ? raw.normalize("NFC").replace(/\p{Cf}/gu, "").trim().toLowerCase() : ""
  if (!value || value.length > 254 || !EMAIL_RE.test(value)) return null
  return value
}

/**
 * The JOIN KEY: trimmed and lowercased, never validated, "" for a missing
 * value. This is how a row is matched by email (a lead to its booking, a Stripe
 * customer to its subscription row, the canary to its own address), so it must
 * accept whatever is already stored. Validation belongs at the door, in
 * `normalizeEmailAddress`; a key that rejected would orphan every stored row
 * whose address the format check dislikes. Used to be seven identical private
 * copies.
 */
export function normalizeEmailKey(raw: string | null | undefined): string {
  return (raw ?? "").trim().toLowerCase()
}

/**
 * Escape a value for safe interpolation into HTML.
 *
 * One home for this, deliberately. It used to be reimplemented in twelve files
 * at THREE different widths: some escaped only `& < >`, some added `"`, some
 * added `'`. That divergence was shipping in live email. The narrow variants
 * are the dangerous ones: a value carrying a double quote breaks out of an HTML
 * attribute (`href="…"`, `alt="…"`), and these senders interpolate untrusted
 * text (booking titles, attendee names, lead answers, generated copy).
 *
 * This is the widest form, which is the safe one. Escaping `"` and `'` in a
 * text node is harmless because the entities render as the literal characters,
 * so widening changed no visible output.
 *
 * Escape BEFORE converting newlines to <br>, and only ever render a value as an
 * href once it is confirmed to be a valid http(s) URL (see `safeHttpUrl`
 * below).
 */
export function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}

/**
 * The URL back out (normalized) only when it is a genuine http(s) URL. Null for
 * `javascript:`, `data:`, any other scheme, and any malformed string.
 *
 * Lives beside `escapeHtml` because it is the other half of the same rule: a
 * value is escaped before it reaches a text node, and passed through here
 * before it reaches an href. Both are called from email senders, the SMS
 * reminder lane, and anything else that puts a booking-supplied link in front
 * of a person, so a second private copy is a second width to get wrong.
 */
export function safeHttpUrl(value: string | null | undefined): string | null {
  if (!value) return null
  try {
    const url = new URL(value)
    if (url.protocol !== "http:" && url.protocol !== "https:") return null
    // The URL parser leaves a quote or angle bracket in a hostname as it is,
    // and that would break out of an href attribute. No real host has one.
    if (!/^(\[[0-9a-f:.]+\]|[a-z0-9.-]+)$/i.test(url.hostname)) return null
    return url.toString()
  } catch {
    return null
  }
}
